import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import es from "./es.json";
import en from "./en.json";
import de from "./de.json";
import fr from "./fr.json";
import pt from "./pt.json";

const LANG_STORAGE_KEY = "mac_lang";

const translations = { es, en, de, fr, pt };

const LanguageContext = createContext();

function getByPath(obj, path) {
  return path.split(".").reduce((acc, part) => (acc ? acc[part] : undefined), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("es");

  // cargar idioma desde localStorage o navegador
  useEffect(() => {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved && translations[saved]) {
      setLang(saved);
    } else {
       const nav = (navigator.language || "").toLowerCase(); // ej "de-DE"
      const browserLang =
        nav.startsWith("es") ? "es" :
        nav.startsWith("de") ? "de" :
        nav.startsWith("fr") ? "fr" :
        nav.startsWith("pt") ? "pt" :
        "en";
      setLang(browserLang);
    }
  }, []);

  const value = useMemo(() => {
    const t = (key, params) => {
      const dict = translations[lang] || translations.es;

      // 1) Buscar clave como anidada: "layout.sidebar.title"
      let val = getByPath(dict, key);

      // 2) Fallback: si tienes claves planas por compatibilidad
      if (val === undefined) val = dict[key];

      // 3) Si no existe, regresa la key (para detectar faltantes fácil)
      if (typeof val !== "string") return key;

      // 4) Reemplazo simple de params: "Hola {name}"
      if (!params) return val;
      return val
        .replace(/\{\{(\w+)\}\}/g, (_, k) => (params[k] ?? `{{${k}}}`))
        .replace(/\{(\w+)\}/g, (_, k) => (params[k] ?? `{${k}}`));
    };

    const changeLanguage = (newLang) => {
      if (!translations[newLang]) return;
      setLang(newLang);
      localStorage.setItem(LANG_STORAGE_KEY, newLang);
    };

    return { lang, t, setLang: changeLanguage };
  }, [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
